import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, AlertTriangle, Database, RefreshCw, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SchemaInfo {
  environment: string;
  hasLegacySchema: boolean;
  needsMigration: boolean;
  columns: {
    providerType: string;
    status: string;
    isDefault?: string;
  };
}

interface SchemaHealth {
  success: boolean;
  environment: string;
  schemaCompatible: boolean;
  emailSettingsCount: number;
  hasLegacySchema: boolean;
  message: string;
}

export default function SchemaManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Get schema information
  const { data: schemaInfo, isLoading: schemaLoading, error: schemaError } = useQuery<{
    success: boolean;
    schemaInfo: SchemaInfo;
  }>({
    queryKey: ["/api/admin/schema/info"],
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Get schema health
  const { data: schemaHealth, isLoading: healthLoading } = useQuery<SchemaHealth>({
    queryKey: ["/api/admin/schema/health"],
    refetchInterval: 60000, // Refresh every minute
  });

  // Schema migration mutation
  const migrationMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/admin/schema/migrate");
      return response.json();
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: "Migration Successful",
          description: data.message,
        });
        // Refresh all schema-related queries
        queryClient.invalidateQueries({ queryKey: ["/api/admin/schema/info"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/schema/health"] });
      } else {
        toast({
          title: "Migration Failed", 
          description: data.message,
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Migration Error",
        description: error.message || "Failed to migrate schema",
        variant: "destructive",
      });
    },
  });

  const handleMigration = () => {
    if (confirm("Are you sure you want to migrate the database schema? This will update column names and structure.")) {
      migrationMutation.mutate();
    }
  };

  const getEnvironmentColor = (env: string) => {
    switch (env) {
      case 'replit': return 'bg-blue-100 text-blue-800';
      case 'docker': return 'bg-green-100 text-green-800';
      case 'aws': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (schemaLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading schema information...</span>
        </div>
      </div>
    );
  }

  if (schemaError) {
    return (
      <div className="p-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load schema information. Please check your connection and try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const info = schemaInfo?.schemaInfo;
  
  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Database Schema Management</h1>
        <p className="text-muted-foreground">
          Manage database schema compatibility across development and production environments
        </p>
      </div>

      {/* Environment Overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Environment Overview
          </CardTitle>
          <CardDescription>
            Current database environment and schema status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-medium">Current Environment:</span>
            <Badge className={getEnvironmentColor(info?.environment || 'unknown')}>
              {info?.environment?.toUpperCase() || 'Unknown'}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="font-medium">Schema Type:</span>
            <Badge variant={info?.hasLegacySchema ? "secondary" : "default"}>
              {info?.hasLegacySchema ? "Legacy Schema" : "Modern Schema"}
            </Badge>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="font-medium">Migration Status:</span>
            <div className="flex items-center gap-2">
              {info?.needsMigration ? (
                <>
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span className="text-yellow-600">Migration Required</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-green-600">Up to Date</span>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Schema Health Status */}
      {schemaHealth && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Schema Health Check
            </CardTitle>
            <CardDescription>
              Real-time compatibility and performance status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">Compatibility Status:</span>
              <div className="flex items-center gap-2">
                {schemaHealth.schemaCompatible ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-green-600">Compatible</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-red-600">Issues Detected</span>
                  </>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="font-medium">Email Settings Count:</span>
              <Badge variant="outline">{schemaHealth.emailSettingsCount}</Badge>
            </div>
            
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{schemaHealth.message}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Column Mapping Details */}
      {info?.columns && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Column Mapping</CardTitle>
            <CardDescription>
              Current database column structure for email_settings table
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-mono text-sm">Provider Type:</span>
                <code className="bg-muted px-2 py-1 rounded text-sm">{info.columns.providerType}</code>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="font-mono text-sm">Status:</span>
                <code className="bg-muted px-2 py-1 rounded text-sm">{info.columns.status}</code>
              </div>
              
              {info.columns.isDefault && (
                <div className="flex justify-between items-center">
                  <span className="font-mono text-sm">Is Default:</span>
                  <code className="bg-muted px-2 py-1 rounded text-sm">{info.columns.isDefault}</code>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Migration Actions */}
      {info?.needsMigration && (
        <Card>
          <CardHeader>
            <CardTitle className="text-orange-600">Migration Required</CardTitle>
            <CardDescription>
              Your database schema needs to be updated to the modern format for full compatibility
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Migration will update column names from legacy format (provider, is_active) to modern format (provider_type, status).
                All existing data will be preserved.
              </AlertDescription>
            </Alert>
            
            <div className="flex gap-4">
              <Button 
                onClick={handleMigration}
                disabled={migrationMutation.isPending}
                className="flex items-center gap-2"
              >
                {migrationMutation.isPending ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Database className="h-4 w-4" />
                )}
                {migrationMutation.isPending ? "Migrating..." : "Migrate Schema"}
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ["/api/admin/schema/info"] });
                  queryClient.invalidateQueries({ queryKey: ["/api/admin/schema/health"] });
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {info && !info.needsMigration && (
        <Card>
          <CardHeader>
            <CardTitle className="text-green-600 flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Schema Up to Date
            </CardTitle>
            <CardDescription>
              Your database schema is using the modern format and is fully compatible
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Button 
                variant="outline"
                onClick={() => {
                  queryClient.invalidateQueries({ queryKey: ["/api/admin/schema/info"] });
                  queryClient.invalidateQueries({ queryKey: ["/api/admin/schema/health"] });
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}